/**
 * Хук загрузки и производных данных посадки.
 * Объединяет запросы к API (planting, properties, simulation)
 * и вычисляет производные значения (activeLocation, forecastMemos, etc.).
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import { useGetPlantingByIdQuery } from '../../../../api/plantingsApi';
import { useGetPlantingPropertiesQuery } from '../../../../api/plantingEventsApi';
import {
  useGetSaveRuntimeSessionQuery,
  useGetEntityTimeseriesQuery,
} from '../../../../api/simulationApi';
import { selectIsAuthenticated } from '../../../../store/slices/authSlice';
import { TIMESERIES_PROPERTIES } from '../constants';
import { extractWeatherGap } from '../../../../components/weather/extractWeatherGap';

const DAYS_PER_YEAR = 365.25;

const toFiniteNumber = (value: unknown): number | null => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeAgeDisplayUnit = (value: unknown): 'days' | 'years' | null => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'days' || normalized === 'years' ? normalized : null;
};

export function usePlantingData() {
  const { saveId, plantingId } = useParams<{ saveId: string; plantingId: string }>();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const {
    data: basePlanting,
    isLoading,
    isError,
    refetch: refetchBasePlanting,
  } = useGetPlantingByIdQuery(saveId && plantingId ? { saveId, id: plantingId } : skipToken);

  const resolvedFarmId = basePlanting?.locations?.[0]?.farm?.id
    ? String(basePlanting.locations[0].farm.id)
    : undefined;

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(
    isAuthenticated && saveId ? saveId : skipToken,
  );
  const simSessionId = activeSimSession?.id ?? null;
  const simSaveId = activeSimSession?.saveId ?? null;
  const simDisplayDate = activeSimSession?.simDate ?? null;

  const { data: simPlanting, refetch: refetchSimPlanting } = useGetPlantingByIdQuery(
    saveId && plantingId && simSessionId ? { saveId, id: plantingId, simSessionId } : skipToken,
  );
  const planting = (simSessionId ? simPlanting : undefined) ?? basePlanting;

  const activeLocation = useMemo(() => {
    const locations = Array.isArray(planting?.locations) ? planting.locations : [];
    if (locations.length === 0) return null;
    const current = locations.find((loc) => !loc?.toDate);
    if (current) return current;
    return [...locations].sort((a, b) => {
      const aTs = a?.fromDate ? new Date(a.fromDate).getTime() : 0;
      const bTs = b?.fromDate ? new Date(b.fromDate).getTime() : 0;
      return bTs - aTs;
    })[0] || null;
  }, [planting?.locations]);

  const {
    data: attributes = [],
    isLoading: loadingAttributes,
    refetch: refetchAttributes,
  } = useGetPlantingPropertiesQuery(
    saveId && plantingId ? { saveId, plantingId, simSessionId } : skipToken,
  );

  const {
    data: forecastSeries,
    isFetching: loadingForecastSeries,
    error: forecastSeriesError,
    refetch: refetchForecastSeries,
  } = useGetEntityTimeseriesQuery(
    {
      sessionId: simSessionId || '',
      entityType: 'planting',
      entityId: plantingId || '',
      properties: TIMESERIES_PROPERTIES,
    },
    { skip: !simSessionId || !plantingId, refetchOnFocus: true },
  );

  const forecastWeatherGap = useMemo(() => extractWeatherGap(forecastSeriesError), [forecastSeriesError]);

  // ── Производные данные симуляции ──

  const notPlantedDays = useMemo(() => {
    const points = forecastSeries?.timeSeries || [];
    return points.filter((p) => p?.vegetationStage === 'not_planted').length;
  }, [forecastSeries?.timeSeries]);

  const lifecycleTerminalPoint = useMemo(() => {
    const points = forecastSeries?.timeSeries || [];
    if (!points.length) return null;
    const terminalDate = forecastSeries?.stats?.terminalDate
      || forecastSeries?.stats?.completionDate
      || forecastSeries?.stats?.deathDate
      || null;
    if (terminalDate) {
      const byDate = points.find((point) => point?.date === terminalDate);
      if (byDate) return byDate;
    }
    return points.find((p) => p?.vegetationStage === 'dead' || p?.vegetationStage === 'completed') || null;
  }, [
    forecastSeries?.timeSeries,
    forecastSeries?.stats?.terminalDate,
    forecastSeries?.stats?.completionDate,
    forecastSeries?.stats?.deathDate,
  ]);

  const isForecastAnnualLike = useMemo(() => {
    const explicitFinalState = forecastSeries?.finalState?.isAnnualLike;
    if (typeof explicitFinalState === 'boolean') return explicitFinalState;

    const explicitTerminalPoint = lifecycleTerminalPoint?.isAnnualLike;
    if (typeof explicitTerminalPoint === 'boolean') return explicitTerminalPoint;

    const ageDisplayUnit = normalizeAgeDisplayUnit(lifecycleTerminalPoint?.ageDisplayUnit)
      || normalizeAgeDisplayUnit(forecastSeries?.finalState?.ageDisplayUnit);
    if (ageDisplayUnit) return ageDisplayUnit === 'days';

    const lifecycleType = forecastSeries?.finalState?.lifecycle?.type;
    if (lifecycleType) return lifecycleType === 'annual';

    const plantType = String(forecastSeries?.plantType || '').toLowerCase();
    return plantType === 'annual' || plantType === 'biennial';
  }, [
    forecastSeries?.finalState?.isAnnualLike,
    forecastSeries?.finalState?.ageDisplayUnit,
    forecastSeries?.finalState?.lifecycle?.type,
    forecastSeries?.plantType,
    lifecycleTerminalPoint?.isAnnualLike,
    lifecycleTerminalPoint?.ageDisplayUnit,
  ]);

  const lifecycleTerminalInfo = useMemo(() => {
    const terminalStatus = forecastSeries?.stats?.terminalStatus
      || (forecastSeries?.stats?.isDead ? 'dead' : (forecastSeries?.stats?.isCompleted ? 'completed' : null));
    if (!terminalStatus) return null;
    const terminalDate = forecastSeries?.stats?.terminalDate
      || forecastSeries?.stats?.completionDate
      || forecastSeries?.stats?.deathDate
      || lifecycleTerminalPoint?.date
      || null;
    const terminalReason = forecastSeries?.stats?.terminalReason
      || forecastSeries?.stats?.completionCause
      || forecastSeries?.stats?.deathCause
      || null;
    return { terminalStatus, terminalDate, terminalReason };
  }, [
    forecastSeries?.stats?.terminalStatus,
    forecastSeries?.stats?.terminalDate,
    forecastSeries?.stats?.terminalReason,
    forecastSeries?.stats?.isDead,
    forecastSeries?.stats?.isCompleted,
    forecastSeries?.stats?.completionDate,
    forecastSeries?.stats?.deathDate,
    forecastSeries?.stats?.completionCause,
    forecastSeries?.stats?.deathCause,
    lifecycleTerminalPoint?.date,
  ]);

  const simulatedAgeDisplay = useMemo(() => {
    const terminalAgeDisplayUnit = normalizeAgeDisplayUnit(lifecycleTerminalPoint?.ageDisplayUnit);
    const terminalAgeDisplayValue = toFiniteNumber(lifecycleTerminalPoint?.ageDisplayValue);
    if (terminalAgeDisplayUnit && terminalAgeDisplayValue != null) {
      if (isForecastAnnualLike && terminalAgeDisplayUnit === 'years') {
        return { unit: 'days' as const, value: Math.round(terminalAgeDisplayValue * DAYS_PER_YEAR) };
      }
      return { unit: terminalAgeDisplayUnit, value: terminalAgeDisplayValue };
    }

    const finalAgeDisplayUnit = normalizeAgeDisplayUnit(forecastSeries?.finalState?.ageDisplayUnit);
    const finalAgeDisplayValue = toFiniteNumber(forecastSeries?.finalState?.ageDisplayValue);
    if (finalAgeDisplayUnit && finalAgeDisplayValue != null) {
      if (isForecastAnnualLike && finalAgeDisplayUnit === 'years') {
        return { unit: 'days' as const, value: Math.round(finalAgeDisplayValue * DAYS_PER_YEAR) };
      }
      return { unit: finalAgeDisplayUnit, value: finalAgeDisplayValue };
    }

    if (isForecastAnnualLike) {
      const lifecycleAgeDays = toFiniteNumber(lifecycleTerminalPoint?.ageDays);
      if (lifecycleAgeDays != null) return { unit: 'days' as const, value: lifecycleAgeDays };

      const ageDays = toFiniteNumber(forecastSeries?.finalState?.ageDays);
      if (ageDays != null) return { unit: 'days' as const, value: ageDays };
    }

    const lifecycleAgeYears = toFiniteNumber(lifecycleTerminalPoint?.ageYears);
    if (lifecycleAgeYears != null) return { unit: 'years' as const, value: lifecycleAgeYears };

    const ageYears = toFiniteNumber(forecastSeries?.finalState?.ageYears);
    if (ageYears != null) return { unit: 'years' as const, value: ageYears };

    const lifecycleAgeDays = toFiniteNumber(lifecycleTerminalPoint?.ageDays);
    if (lifecycleAgeDays != null) return { unit: 'days' as const, value: lifecycleAgeDays };

    const ageDays = toFiniteNumber(forecastSeries?.finalState?.ageDays);
    if (ageDays != null) return { unit: 'days' as const, value: ageDays };

    return null;
  }, [
    forecastSeries?.finalState?.ageDays,
    forecastSeries?.finalState?.ageDisplayUnit,
    forecastSeries?.finalState?.ageDisplayValue,
    forecastSeries?.finalState?.ageYears,
    isForecastAnnualLike,
    lifecycleTerminalPoint?.ageDays,
    lifecycleTerminalPoint?.ageDisplayUnit,
    lifecycleTerminalPoint?.ageDisplayValue,
    lifecycleTerminalPoint?.ageYears,
  ]);

  const simulatedAgeDisplayUnit = simulatedAgeDisplay?.unit ?? null;
  const simulatedAgeDisplayValue = simulatedAgeDisplay?.value ?? null;

  const simulatedFinalAgeYears = useMemo(() => {
    if (!simulatedAgeDisplay) return null;
    return simulatedAgeDisplay.unit === 'years'
      ? simulatedAgeDisplay.value
      : simulatedAgeDisplay.value / DAYS_PER_YEAR;
  }, [simulatedAgeDisplay]);

  const simulatedFinalAgeDays = useMemo(() => {
    if (!simulatedAgeDisplay) return null;
    return simulatedAgeDisplay.unit === 'days'
      ? simulatedAgeDisplay.value
      : simulatedAgeDisplay.value * DAYS_PER_YEAR;
  }, [simulatedAgeDisplay]);

  const simulatedAgeDisplayDateLabel = useMemo(() => {
    if (!simSessionId) return null;
    const lifecycleDate = lifecycleTerminalInfo?.terminalDate || null;
    if (!lifecycleDate) return null;
    return `${dayjs(lifecycleDate as string).format('DD.MM.YYYY')} · завершение жизненного цикла (симуляция)`;
  }, [simSessionId, lifecycleTerminalInfo?.terminalDate]);

  const harvestForecast = useMemo(() => {
    return forecastSeries?.finalState?.harvestForecast || null;
  }, [forecastSeries?.finalState?.harvestForecast]);

  const harvestGrossTargetKg = useMemo(() => {
    const value = Number(harvestForecast?.expectedYieldPerPlantKg);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [harvestForecast?.expectedYieldPerPlantKg]);

  const harvestMarketableTargetKg = useMemo(() => {
    if (harvestGrossTargetKg == null) return null;
    const marketableFraction = Number(harvestForecast?.marketableFraction);
    if (!Number.isFinite(marketableFraction)) return harvestGrossTargetKg;
    return harvestGrossTargetKg * Math.max(0, Math.min(1, marketableFraction));
  }, [harvestForecast?.marketableFraction, harvestGrossTargetKg]);

  const plantingQuantity = useMemo(() => {
    const qty = Number(planting?.quantity || 1);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }, [planting?.quantity]);

  const showBatchQuantityMetric = useMemo(() => {
    if (planting?.plantingType === 'batch') return true;
    const points = forecastSeries?.timeSeries || [];
    return points.some((point: any) => {
      const qty = Number(point?.currentQuantity);
      return Number.isFinite(qty) && qty > 1;
    });
  }, [forecastSeries?.timeSeries, planting?.plantingType]);

  /** Projected quantity from last pre-session entry (simulated batch loss) */
  const projectedQuantity = useMemo(() => {
    const points = forecastSeries?.timeSeries || [];
    if (points.length === 0) return null;
    // Take the last pre-session entry, or fall back to first session entry
    const preSessionPoints = points.filter((p: any) => p?.preSession);
    const lastPreSession = preSessionPoints.length > 0
      ? preSessionPoints[preSessionPoints.length - 1]
      : null;
    const qty = Number(lastPreSession?.currentQuantity);
    if (!Number.isFinite(qty) || qty <= 0) return null;
    const initial = Number(planting?.quantity || 1);
    return qty < initial ? qty : null;
  }, [forecastSeries?.timeSeries, planting?.quantity]);

  const displayedAttributes = useMemo(() => {
    if (!simSessionId) return attributes;
    const hasAgeDays = attributes.some((attr) => attr.propertyCode === 'age_days');
    const hasAgeYears = attributes.some((attr) => attr.propertyCode === 'age_years');
    if (!(hasAgeDays && hasAgeYears)) return attributes;
    const preferredAgeCode = simulatedAgeDisplayUnit === 'days'
      ? 'age_days'
      : simulatedAgeDisplayUnit === 'years'
        ? 'age_years'
        : (isForecastAnnualLike ? 'age_days' : 'age_years');
    return attributes.filter((attr) => {
      if (preferredAgeCode === 'age_days') return attr.propertyCode !== 'age_years';
      return attr.propertyCode !== 'age_days';
    });
  }, [attributes, simSessionId, simulatedAgeDisplayUnit, isForecastAnnualLike]);

  const reloadPlantingDerivedData = async () => {
    const tasks: Array<Promise<any>> = [refetchBasePlanting(), refetchAttributes()];
    if (simSessionId) {
      tasks.push(refetchSimPlanting());
      tasks.push(refetchForecastSeries());
    }
    await Promise.all(tasks);
  };

  const farmName = planting?.locations?.[0]?.farm?.name
    || (resolvedFarmId ? `Ферма ${resolvedFarmId}` : 'Ферма');
  const currentLocation = planting?.locations?.find((l) => !l.toDate) ?? planting?.locations?.[0];

  return {
    plantingId,
    planting,
    isLoading,
    isError,
    resolvedFarmId,
    simSessionId,
    simSaveId,
    simDisplayDate,
    activeLocation,
    attributes,
    displayedAttributes,
    loadingAttributes,
    forecastSeries,
    loadingForecastSeries,
    forecastWeatherGap,
    refetchForecastSeries,
    isForecastAnnualLike,
    notPlantedDays,
    lifecycleTerminalInfo,
    simulatedAgeDisplayUnit,
    simulatedAgeDisplayValue,
    simulatedFinalAgeYears,
    simulatedFinalAgeDays,
    simulatedAgeDisplayDateLabel,
    harvestForecast,
    harvestGrossTargetKg,
    harvestMarketableTargetKg,
    plantingQuantity,
    showBatchQuantityMetric,
    projectedQuantity,
    farmName,
    currentLocation,
    reloadPlantingDerivedData,
  };
}
